import apiService from './apiService';
import { getImageUrl } from '../utils/imageUrl';

/**
 * Service for fetching customer-facing Dedicated Team section configuration
 * and active/published team members from the backend API.
 *
 * Public endpoints supported:
 *   - Section visibility config: GET /api/ecommerce/team/config
 *   - Published team members:    GET /api/ecommerce/team
 */
class TeamService {
    constructor() {
        // Tracks whether standalone config endpoint returned 404 in this session;
        // automatically reset whenever a real-time sync check is triggered.
        this._configRouteMissing = false;
    }

    /**
     * Parse an explicit boolean-like visibility flag without guessing from unrelated values.
     * @param {any} value
     * @returns {boolean|null}
     */
    parseBooleanFlag(value) {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') {
            if (value === 1) return true;
            if (value === 0) return false;
        }
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (
                normalized === 'true' ||
                normalized === '1' ||
                normalized === 'enabled' ||
                normalized === 'visible'
            ) {
                return true;
            }
            if (
                normalized === 'false' ||
                normalized === '0' ||
                normalized === 'disabled' ||
                normalized === 'hidden'
            ) {
                return false;
            }
        }
        return null;
    }

    /**
     * Extract section visibility boolean from a dedicated section config API response.
     * @param {any} response
     * @returns {boolean|null}
     */
    extractVisibilityFromConfigResponse(response) {
        if (!response || typeof response !== 'object') return null;

        const candidateContainers = [
            response?.data?.section,
            response?.data?.config,
            response?.data?.sectionConfig,
            response?.data?.section_config,
            response?.data?.teamSection,
            response?.data?.team_section,
            response?.data?.dedicatedTeam,
            response?.data?.dedicated_team,
            response?.data?.settings?.dedicatedTeam,
            response?.data?.settings?.dedicated_team,
            response?.data?.settings?.teamSection,
            response?.data?.settings?.team_section,
            response?.data,
            response?.section,
            response?.config,
            response?.sectionConfig,
            response?.section_config,
            response,
        ];

        const candidateKeys = [
            'isEnabled',
            'is_enabled',
            'isSectionEnabled',
            'is_section_enabled',
            'teamSectionEnabled',
            'team_section_enabled',
            'showTeamSection',
            'show_team_section',
            'isVisible',
            'is_visible',
            'enabled',
            'visible',
        ];

        for (const container of candidateContainers) {
            if (!container || typeof container !== 'object' || Array.isArray(container)) {
                continue;
            }
            for (const key of candidateKeys) {
                if (Object.prototype.hasOwnProperty.call(container, key)) {
                    const parsed = this.parseBooleanFlag(container[key]);
                    if (parsed !== null) {
                        return parsed;
                    }
                }
            }
        }

        return null;
    }

    /**
     * Extract section visibility boolean if embedded in the /ecommerce/team response envelope.
     * Kept strictly separate from individual team member publication fields.
     * @param {any} response
     * @returns {boolean|null}
     */
    extractVisibilityFromTeamResponse(response) {
        if (!response || typeof response !== 'object' || Array.isArray(response)) {
            return null;
        }

        const nestedSectionContainers = [
            response?.data?.section,
            response?.data?.sectionConfig,
            response?.data?.section_config,
            response?.data?.config,
            response?.data?.teamSection,
            response?.data?.team_section,
            response?.section,
            response?.sectionConfig,
            response?.section_config,
            response?.config,
        ];

        const nestedKeys = [
            'isEnabled',
            'is_enabled',
            'isVisible',
            'is_visible',
            'enabled',
            'visible',
        ];

        for (const container of nestedSectionContainers) {
            if (!container || typeof container !== 'object' || Array.isArray(container)) {
                continue;
            }
            for (const key of nestedKeys) {
                if (Object.prototype.hasOwnProperty.call(container, key)) {
                    const parsed = this.parseBooleanFlag(container[key]);
                    if (parsed !== null) {
                        return parsed;
                    }
                }
            }
        }

        const topContainers = [response?.data, response];
        const explicitTopKeys = [
            'isSectionEnabled',
            'is_section_enabled',
            'sectionEnabled',
            'section_enabled',
            'teamSectionEnabled',
            'team_section_enabled',
            'showTeamSection',
            'show_team_section',
            'isEnabled',
            'is_enabled',
        ];

        for (const container of topContainers) {
            if (!container || typeof container !== 'object' || Array.isArray(container)) {
                continue;
            }
            for (const key of explicitTopKeys) {
                if (Object.prototype.hasOwnProperty.call(container, key)) {
                    const parsed = this.parseBooleanFlag(container[key]);
                    if (parsed !== null) {
                        return parsed;
                    }
                }
            }
        }

        return null;
    }

    /**
     * Extract section visibility boolean from a real-time SSE event payload if present.
     * @param {any} payload
     * @returns {boolean|null}
     */
    extractVisibilityFromRealtimePayload(payload) {
        if (!payload || typeof payload !== 'object') return null;

        const entity = String(payload.entity || payload.type || payload.resource || '')
            .trim()
            .toLowerCase();
        const action = String(payload.action || payload.event || '')
            .trim()
            .toLowerCase();

        // Only read direct visibility flags from section-level events (never individual member toggles)
        const isSectionLevelEvent =
            entity === 'team_section' ||
            entity === 'team-section' ||
            entity === 'dedicated_team' ||
            action.includes('section') ||
            action.includes('visibility') ||
            action.includes('config');

        if (!isSectionLevelEvent) {
            return null;
        }

        return this.extractVisibilityFromConfigResponse(payload);
    }

    /**
     * Allow re-probing the standalone config endpoint (e.g. when an SSE event arrives).
     */
    resetConfigProbe() {
        this._configRouteMissing = false;
    }

    /**
     * Fetch the Dedicated Team section visibility configuration from the backend.
     * @param {object} options
     * @param {boolean} [options.forceProbe=false]
     * @returns {Promise<{ isEnabled: boolean|null, configured: boolean, error: boolean }>}
     */
    async getSectionConfig({ forceProbe = false } = {}) {
        if (forceProbe) {
            this._configRouteMissing = false;
        }

        if (this._configRouteMissing) {
            return { isEnabled: null, configured: false, error: false };
        }

        try {
            const response = await apiService.get('/ecommerce/team/config');
            const parsed = this.extractVisibilityFromConfigResponse(response);
            if (parsed !== null) {
                return { isEnabled: parsed, configured: true, error: false };
            }
            return { isEnabled: null, configured: false, error: false };
        } catch (err) {
            if (err && Number(err.status) === 404) {
                this._configRouteMissing = true;
                return { isEnabled: null, configured: false, error: false };
            }
            return { isEnabled: null, configured: false, error: true };
        }
    }

    /**
     * Normalize raw team member object from backend.
     * Individual member publication status (`is_active`) is independent of section visibility.
     */
    normalizeMember(item) {
        if (!item || typeof item !== 'object') return null;

        const rawOrder = item.display_order ?? item.displayOrder ?? item.order ?? 0;
        const displayOrder = Number.isFinite(Number(rawOrder)) ? Number(rawOrder) : 0;

        const rawStatus = (item.status || '').toLowerCase();
        const isActive =
            item.is_active !== false &&
            item.isActive !== false &&
            item.isPublished !== false &&
            item.is_published !== false &&
            rawStatus !== 'inactive' &&
            rawStatus !== 'unpublished' &&
            rawStatus !== 'draft';

        const rawImage = item.image || item.profile_image || item.profileImage || '';
        const image = rawImage
            ? getImageUrl(rawImage) || rawImage
            : '/assets/media/images/team/ceo.jpg';

        return {
            id: String(item._id || item.id || `team-${Math.random()}`),
            name: item.name || item.fullName || '',
            role: item.role || item.designation || item.title || '',
            experience: item.experience || item.tenure || '',
            image,
            bio: item.bio || item.biography || item.description || '',
            badge: (item.badge || item.category || '').trim(),
            display_order: displayOrder,
            is_active: isActive,
        };
    }

    /**
     * Extract and normalize active/published team members from raw API response.
     * @param {any} response
     * @returns {Array}
     */
    extractNormalizedMembers(response) {
        const findArray = (obj) => {
            if (!obj) return null;
            if (Array.isArray(obj)) return obj;
            if (typeof obj !== 'object') return null;

            const candidateKeys = [
                'team_members',
                'teamMembers',
                'team',
                'members',
                'data',
                'items',
                'results',
                'rows',
            ];

            for (const key of candidateKeys) {
                if (Array.isArray(obj[key])) {
                    return obj[key];
                }
            }
            return null;
        };

        let rawList = findArray(response);
        if (!rawList && response && response.data) {
            rawList = findArray(response.data);
        }

        if (!Array.isArray(rawList) || rawList.length === 0) {
            return [];
        }

        return rawList
            .map((item) => this.normalizeMember(item))
            .filter((member) => member && member.is_active)
            .sort((a, b) => a.display_order - b.display_order);
    }

    /**
     * Internal helper to fetch raw team endpoint response across supported public routes.
     * @returns {Promise<any>}
     */
    async fetchTeamResponse() {
        const endpoints = ['/ecommerce/team', '/ecommerce/team-members', '/team', '/team-members'];
        let lastErr = null;

        for (const endpoint of endpoints) {
            try {
                return await apiService.get(endpoint);
            } catch (err) {
                lastErr = err;
                // Stop falling back to protected /team routes if public /ecommerce/team gave a server/network error
                // or if fallback returned 401/403
                if (err && (err.status === 401 || err.status === 403)) {
                    break;
                }
            }
        }

        throw lastErr || new Error('Failed to fetch team members');
    }

    /**
     * Fetch all active team members for the customer-facing frontend.
     * @returns {Promise<Array>} Array of active team members sorted by display_order
     */
    async getActiveTeamMembers() {
        try {
            const response = await this.fetchTeamResponse();
            return this.extractNormalizedMembers(response);
        } catch (error) {
            console.warn(
                '[TeamService] Failed to fetch team members from API:',
                error?.message || error
            );
            return [];
        }
    }

    /**
     * Fetch both the Dedicated Team section visibility configuration and active team members.
     * Keeps section-level visibility strictly separate from individual member publication status,
     * while gracefully handling missing configuration or API errors without dummy data.
     *
     * @param {object} options
     * @param {boolean} [options.forceConfigProbe=false]
     * @returns {Promise<{
     *   isEnabled: boolean,
     *   isConfigured: boolean,
     *   rawIsEnabled: boolean|null,
     *   members: Array,
     *   hasError: boolean
     * }>}
     */
    async getTeamSectionData({ forceConfigProbe = false } = {}) {
        const [configSettled, teamSettled] = await Promise.allSettled([
            this.getSectionConfig({ forceProbe: forceConfigProbe }),
            this.fetchTeamResponse(),
        ]);

        const configResult =
            configSettled.status === 'fulfilled'
                ? configSettled.value
                : { isEnabled: null, configured: false, error: true };

        const teamSuccess = teamSettled.status === 'fulfilled';
        const teamResponse = teamSuccess ? teamSettled.value : null;

        // 1. Prefer dedicated section config endpoint; fall back to section metadata in /ecommerce/team
        let rawIsEnabled = configResult.isEnabled;
        if (rawIsEnabled === null && teamResponse) {
            rawIsEnabled = this.extractVisibilityFromTeamResponse(teamResponse);
        }

        // 2. If admin explicitly disabled the section, hide it completely (preserving DB records untouched)
        if (rawIsEnabled === false) {
            return {
                isEnabled: false,
                isConfigured: true,
                rawIsEnabled: false,
                members: teamResponse ? this.extractNormalizedMembers(teamResponse) : [],
                hasError: false,
            };
        }

        const members = teamResponse ? this.extractNormalizedMembers(teamResponse) : [];

        // 3. If admin explicitly enabled the section, show it independently of member count
        if (rawIsEnabled === true) {
            return {
                isEnabled: true,
                isConfigured: true,
                rawIsEnabled: true,
                members,
                hasError: !teamSuccess,
            };
        }

        // 4. Missing configuration or API unavailable:
        //    - Preserve existing published team members if returned by the API
        //    - Otherwise keep the unconfigured/errored section hidden without dummy data
        if (teamSuccess && members.length > 0) {
            return {
                isEnabled: true,
                isConfigured: false,
                rawIsEnabled: null,
                members,
                hasError: false,
            };
        }

        return {
            isEnabled: false,
            isConfigured: false,
            rawIsEnabled: null,
            members: [],
            hasError: !teamSuccess,
        };
    }
}

const teamService = new TeamService();
export default teamService;
